import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { UsersThree, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, refresh, loading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | ok | error
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setState("error"); setMessage("Missing invitation token."); return; }
    (async () => {
      try {
        const { data } = await api.get(`/company/invites/lookup`, { params: { token } });
        setInvite(data);
        if (data.status !== "pending") {
          setState("error");
          setMessage(
            data.status === "expired" ? "This invitation has expired." :
            data.status === "accepted" ? "This invitation has already been accepted." :
            "This invitation is no longer valid."
          );
        } else {
          setState("ready");
        }
      } catch (e) {
        setState("error");
        setMessage(formatApiError(e?.response?.data?.detail));
      }
    })();
  }, [token]);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/company/accept-invite", { token });
      await refresh();
      setState("ok");
      toast.success(`You joined ${invite?.company_name || "the team"}.`);
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail);
      setState("error");
      setMessage(msg);
    } finally {
      setBusy(false);
    }
  };

  const signInFirstUrl = `/login`; // after login, the user comes back here manually — invite link is in email
  const registerFirstUrl = `/register`;

  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <section className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-md w-full border border-border bg-white p-8">
          <div className="text-xs font-mono tracking-widest-2 uppercase text-muted-foreground mb-3 flex items-center gap-2">
            <UsersThree size={14} weight="duotone" /> Team invitation
          </div>

          {state === "loading" && (
            <>
              <h1 className="font-heading text-2xl font-bold mb-2">Checking your invitation…</h1>
              <p className="text-sm text-muted-foreground">One moment.</p>
            </>
          )}

          {state === "ready" && invite && (
            <>
              <h1 className="font-heading text-2xl font-bold mb-2">
                Join <span className="text-primary">{invite.company_name || "the team"}</span>
              </h1>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                {invite.invited_by_name || "A teammate"} invited <b>{invite.email}</b> to the {invite.company_name || "workspace"} on ExactReach as a <b>{invite.role}</b>.
              </p>
              {!loading && !user && (
                <div className="border border-border bg-muted/40 p-4 text-sm mb-6" data-testid="accept-signin-nudge">
                  Sign in as <b>{invite.email}</b> to accept this invitation.
                  <div className="mt-3 flex gap-2">
                    <Link to={signInFirstUrl}>
                      <Button variant="outline" className="rounded-sm h-9" data-testid="accept-signin-btn">Sign in</Button>
                    </Link>
                    <Link to={registerFirstUrl}>
                      <Button className="rounded-sm h-9" data-testid="accept-register-btn">Create account</Button>
                    </Link>
                  </div>
                </div>
              )}
              {!loading && user && user.email.toLowerCase() !== invite.email.toLowerCase() && (
                <div className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 mb-6" data-testid="accept-wrong-user">
                  You're signed in as <b>{user.email}</b> but this invitation is for <b>{invite.email}</b>. Sign out and sign back in as {invite.email} to accept.
                </div>
              )}
              {!loading && user && user.email.toLowerCase() === invite.email.toLowerCase() && (
                <Button onClick={accept} disabled={busy} className="w-full h-11 rounded-sm" data-testid="accept-invite-btn">
                  {busy ? "Joining…" : `Accept and join ${invite.company_name || "the team"}`}
                </Button>
              )}
            </>
          )}

          {state === "ok" && (
            <>
              <CheckCircle size={40} weight="fill" className="text-primary mb-3" />
              <h1 className="font-heading text-2xl font-bold mb-2">You're in.</h1>
              <p className="text-sm text-muted-foreground">Taking you to the dashboard…</p>
            </>
          )}

          {state === "error" && (
            <>
              <WarningCircle size={40} weight="fill" className="text-muted-foreground mb-3" />
              <h1 className="font-heading text-2xl font-bold mb-2">Invitation not valid.</h1>
              <p className="text-sm text-muted-foreground mb-6" data-testid="accept-invite-error">{message}</p>
              <Link to={user ? "/dashboard" : "/login"}>
                <Button variant="outline" className="rounded-sm">{user ? "Back to dashboard" : "Sign in"}</Button>
              </Link>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
